import React, { FC, useEffect } from "react";
import { Modal, Button, Typography, Form, Cascader } from "antd";
import { ExternalMatter, PracticeArea } from "../../data-types";
import { PriceInput } from "../input-number";
import { useSettings } from "~/hooks";

import styles from "./pms-matters.module.css";
import { VPDLabel } from "~/context/taxonomy-context";

const { number: numberClassName } = styles as any;

export type CreateMatterFromPmsModalProps = {
  open: boolean;
  onClose: () => void;
  externalMatter: ExternalMatter | null;
  onCreateMatterFromPms: (
    externalMatter: ExternalMatter,
    budget: number,
    organisationalUnitId: number
  ) => Promise<void>;
  practiceAreas: PracticeArea[];
  cumulativeAccruedFee: number | null;
};

export const CreateMatterFromPmsModal: FC<CreateMatterFromPmsModalProps> = ({
  open,
  onClose,
  externalMatter,
  onCreateMatterFromPms,
  practiceAreas,
  cumulativeAccruedFee,
}) => {
  const { settings } = useSettings();
  const defaultCurrencySymbol = settings?.currency?.symbol ?? "";
  const [form] = Form.useForm();

  const PA_LABEL = VPDLabel("PracticeArea");

  useEffect(() => {
    const organisationalUnitPath = externalMatter?.organisationalUnit?.id
      ? findPathForOrganisationalUnit(externalMatter.organisationalUnit.id)
      : [];
    form.setFieldValue("practiceAreaId", organisationalUnitPath);
  }, [externalMatter]);

  const paOptions = practiceAreas
    .filter((x) => !x.parent?.id)
    .map((pa) => {
      return {
        value: pa.id,
        label: pa.name,
        children:
          practiceAreas
            .filter((x) => x.parent?.id === pa.id)
            .map((y) => ({ value: y.id, label: y.name })) || [],
      };
    });

  const findPathForOrganisationalUnit = (organisationalUnitId: number) => {
    for (const parent of paOptions) {
      if (parent.value === organisationalUnitId) {
        return [parent.value];
      }
      for (const child of parent.children) {
        if (child.value === organisationalUnitId) {
          return [parent.value, child.value];
        }
      }
    }
    return [];
  };

  return (
    <Modal
      title={`Create proposal${externalMatter?.title ? `: ${externalMatter.title}` : ""}`}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="create"
          type="primary"
          onClick={() => {
            form.submit();
            onClose();
          }}
        >
          Create
        </Button>,
      ]}
    >
      <Form
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 16 }}
        form={form}
        onFinish={() => {
          const orgUnitIdRaw = form.getFieldValue("practiceAreaId");
          const orgUnitId = Array.isArray(orgUnitIdRaw)
            ? orgUnitIdRaw[orgUnitIdRaw.length - 1]
            : orgUnitIdRaw;
          externalMatter &&
            onCreateMatterFromPms(externalMatter, form.getFieldValue("budget"), orgUnitId);
        }}
      >
        <Typography.Paragraph>
          Enter the {PA_LABEL} and budget below to create a Live VPD matter with this information so
          you can track the budget and GPM.
        </Typography.Paragraph>
        <Form.Item label="Client">
          <Typography.Text>{externalMatter?.client?.name}</Typography.Text>
        </Form.Item>
        <Form.Item label="Accrued fee">
          <Typography.Text>
            {externalMatter?.currency?.symbol ?? defaultCurrencySymbol}
            {cumulativeAccruedFee}
          </Typography.Text>
        </Form.Item>
        <Form.Item
          required
          label={`${PA_LABEL}`}
          name="practiceAreaId"
          rules={[
            {
              required: true,
              message: `Please select a ${PA_LABEL}`,
            },
          ]}
        >
          <Cascader
            showSearch={true}
            allowClear
            placeholder={`Select ${PA_LABEL}`}
            options={paOptions}
            disabled={!!externalMatter?.organisationalUnit?.id}
          />
        </Form.Item>
        <Form.Item
          required
          label="Budget"
          name="budget"
          rules={[
            { required: true, message: "Please input the budget!" },
            { type: "number", min: 0, message: "Budget must be greater than 0" },
          ]}
        >
          <PriceInput
            className={numberClassName}
            currencySymbol={externalMatter?.currency?.symbol ?? defaultCurrencySymbol}
            step={100}
            placeholder="Budget"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
